import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
    FiArchive,
    FiLayers,
    FiTruck,
    FiList,
    FiPlus,
    FiArrowRight,
    FiAlertTriangle,
    FiTrendingUp,
    FiTrendingDown,
} from "react-icons/fi";
import { useApp } from "../../../context/AppContext";
import api from "../../../services/api";
import CollegeLogoBadge from "../../../components/common/CollegeLogoBadge";

export default function StoreKeeperDashboard() {
    const { user, academicYear, collegeName } = useApp();
    const navigate = useNavigate();

    const [stats, setStats] = useState({
        totalItems: 0,
        lowStockCount: 0,
        totalCategories: 0,
        totalSuppliers: 0,
    });
    const [recentTransactions, setRecentTransactions] = useState([]);
    const [lowStockItems, setLowStockItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchDashboardData = async () => {
            setLoading(true);
            try {
                const ayParam = academicYear?._id ? { academicYear: academicYear._id } : {};
                const [itemsRes, lowStockRes, txRes, catRes, supRes] = await Promise.allSettled([
                    api.get("/inventory/items", { params: { limit: 100, ...ayParam } }),
                    api.get("/inventory/items/low-stock", { params: ayParam }),
                    api.get("/inventory/transactions", { params: { limit: 5, ...ayParam } }),
                    api.get("/inventory/categories", { params: ayParam }),
                    api.get("/inventory/suppliers"),
                ]);

                const items =
                    itemsRes.status === "fulfilled"
                        ? Array.isArray(itemsRes.value.data)
                            ? itemsRes.value.data
                            : itemsRes.value.data?.data || []
                        : [];

                const totalItems =
                    itemsRes.status === "fulfilled"
                        ? itemsRes.value.data?.pagination?.total ?? items.length
                        : items.length;

                const lowStock =
                    lowStockRes.status === "fulfilled"
                        ? Array.isArray(lowStockRes.value.data)
                            ? lowStockRes.value.data
                            : lowStockRes.value.data?.data || []
                        : items.filter(
                            (item) => (item.currentStock ?? item.quantity ?? 0) <= (item.minStockLevel ?? item.minQuantity ?? 5)
                        );

                const transactions =
                    txRes.status === "fulfilled"
                        ? Array.isArray(txRes.value.data)
                            ? txRes.value.data
                            : txRes.value.data?.data || []
                        : [];

                const categories =
                    catRes.status === "fulfilled"
                        ? Array.isArray(catRes.value.data)
                            ? catRes.value.data
                            : catRes.value.data?.data || []
                        : [];

                const suppliers =
                    supRes.status === "fulfilled"
                        ? Array.isArray(supRes.value.data)
                            ? supRes.value.data
                            : supRes.value.data?.data || []
                        : [];

                setStats({
                    totalItems,
                    lowStockCount: lowStock.length,
                    totalCategories: categories.length,
                    totalSuppliers: suppliers.length,
                });
                setRecentTransactions(transactions.slice(0, 5));
                setLowStockItems(lowStock.slice(0, 5));
            } catch (err) {
                toast.error(err.message || "Failed to load store dashboard data");
            } finally {
                setLoading(false);
            }
        };

        fetchDashboardData();
    }, [academicYear?._id]);

    if (loading) {
        return (
            <div className="loading-center">
                <div className="spinner" />
                Loading storekeeper dashboard…
            </div>
        );
    }

    return (
        <div>
            {/* ── Header ── */}
            <div className="page-header" style={{ flexWrap: "wrap", gap: "1rem", marginBottom: "1.25rem", alignItems: "center", justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.85rem" }}>
                    <CollegeLogoBadge size={52} />
                    <div>
                        <div style={{ fontSize: "11px", fontWeight: 700, color: "#003b67", letterSpacing: "0.04em", textTransform: "uppercase" }}>
                            {collegeName}
                        </div>
                        <h1 className="page-title" style={{ margin: "2px 0 0 0" }}>Welcome back, {user?.displayName || user?.username} 📦</h1>
                        <p className="page-subtitle" style={{ margin: "2px 0 0 0" }}>Central Stores &amp; Inventory Management</p>
                    </div>
                </div>
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                    <button
                        className="btn btn-outline"
                        onClick={() => navigate("/storekeeper/inventory/transactions/new")}
                        style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}
                    >
                        <FiList size={14} /> New Transaction
                    </button>
                    <button
                        className="btn btn-primary"
                        onClick={() => navigate("/storekeeper/inventory/items/new")}
                        style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}
                    >
                        <FiPlus size={14} /> Add New Item
                    </button>
                </div>
            </div>

            {/* ── Low Stock Alert Banner ── */}
            {stats.lowStockCount > 0 && (
                <div
                    style={{
                        background: "#fff7ed",
                        border: "1px solid #ffedd5",
                        borderRadius: 8,
                        padding: "0.85rem 1.1rem",
                        marginBottom: "1.25rem",
                        fontSize: 13,
                        color: "#c2410c",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: "0.5rem",
                    }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        <FiAlertTriangle size={16} />
                        <span>
                            There are <strong>{stats.lowStockCount} item(s)</strong> running low on stock.
                        </span>
                    </div>
                    <button
                        className="btn btn-sm"
                        onClick={() => navigate("/storekeeper/inventory/items?filter=low-stock")}
                        style={{ background: "#ea580c", color: "#fff", border: "none", padding: "4px 10px", fontSize: 12 }}
                    >
                        View Low Stock
                    </button>
                </div>
            )}

            {/* ── KPI Metric Cards ── */}
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
                    gap: "1rem",
                    marginBottom: "1.5rem",
                }}
            >
                <div
                    className="card"
                    style={{ padding: "1.1rem", cursor: "pointer" }}
                    onClick={() => navigate("/storekeeper/inventory/items")}
                >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                        <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)", letterSpacing: "0.05em" }}>
                            Total Items
                        </span>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: "#ffedd5", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <FiArchive size={15} color="#ea580c" />
                        </div>
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 900, color: "var(--text)" }}>
                        {stats.totalItems.toLocaleString("en-IN")}
                    </div>
                    <div style={{ fontSize: 11.5, color: "#ea580c", marginTop: 4, display: "flex", alignItems: "center", gap: 3 }}>
                        Item Catalog <FiArrowRight size={10} />
                    </div>
                </div>

                <div
                    className="card"
                    style={{ padding: "1.1rem", cursor: "pointer" }}
                    onClick={() => navigate("/storekeeper/inventory/items")}
                >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                        <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)", letterSpacing: "0.05em" }}>
                            Low Stock Items
                        </span>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: "#fee2e2", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <FiAlertTriangle size={15} color="#dc2626" />
                        </div>
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 900, color: stats.lowStockCount > 0 ? "#dc2626" : "#16a34a" }}>
                        {stats.lowStockCount}
                    </div>
                    <div style={{ fontSize: 11.5, color: stats.lowStockCount > 0 ? "#dc2626" : "var(--text-muted)", marginTop: 4 }}>
                        {stats.lowStockCount > 0 ? "Restock needed" : "Stock levels adequate ✓"}
                    </div>
                </div>

                <div
                    className="card"
                    style={{ padding: "1.1rem", cursor: "pointer" }}
                    onClick={() => navigate("/storekeeper/inventory/categories")}
                >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                        <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)", letterSpacing: "0.05em" }}>
                            Item Categories
                        </span>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: "#e0f2fe", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <FiLayers size={15} color="#0284c7" />
                        </div>
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 900, color: "var(--text)" }}>
                        {stats.totalCategories.toLocaleString("en-IN")}
                    </div>
                    <div style={{ fontSize: 11.5, color: "#0284c7", marginTop: 4, display: "flex", alignItems: "center", gap: 3 }}>
                        Manage Categories <FiArrowRight size={10} />
                    </div>
                </div>

                <div
                    className="card"
                    style={{ padding: "1.1rem", cursor: "pointer" }}
                    onClick={() => navigate("/storekeeper/inventory/suppliers")}
                >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                        <span style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: "var(--text-muted)", letterSpacing: "0.05em" }}>
                            Registered Suppliers
                        </span>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: "#ede9fe", display: "flex", alignItems: "center", justifyContent: "center" }}>
                            <FiTruck size={15} color="#7c3aed" />
                        </div>
                    </div>
                    <div style={{ fontSize: 24, fontWeight: 900, color: "var(--text)" }}>
                        {stats.totalSuppliers.toLocaleString("en-IN")}
                    </div>
                    <div style={{ fontSize: 11.5, color: "#7c3aed", marginTop: 4, display: "flex", alignItems: "center", gap: 3 }}>
                        Supplier Directory <FiArrowRight size={10} />
                    </div>
                </div>
            </div>

            {/* ── Content Grid: Recent Transactions & Low Stock ── */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: "1.25rem" }}>
                {/* Recent Stock Transactions */}
                <div className="card">
                    <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontWeight: 700 }}>
                            <FiList size={14} color="#ea580c" /> Recent Stock Movements
                        </span>
                        <button
                            className="btn btn-outline btn-sm"
                            onClick={() => navigate("/storekeeper/inventory/transactions")}
                            style={{ display: "flex", alignItems: "center", gap: "0.3rem", fontSize: 12 }}
                        >
                            View All <FiArrowRight size={11} />
                        </button>
                    </div>
                    <div className="card-body" style={{ padding: 0 }}>
                        {recentTransactions.length === 0 ? (
                            <div style={{ padding: "1.5rem", textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
                                No recent stock transactions found.
                            </div>
                        ) : (
                            recentTransactions.map((tx, idx) => {
                                const isPositive = tx.type === "Stock In" || tx.type === "Return" || tx.type === "IN";
                                return (
                                    <div
                                        key={tx._id || idx}
                                        style={{
                                            padding: "0.75rem 1rem",
                                            borderBottom: "1px solid var(--border)",
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            fontSize: 13,
                                        }}
                                    >
                                        <div>
                                            <div style={{ fontWeight: 600 }}>{tx.item?.name || tx.itemName || "Item"}</div>
                                            <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                                                {tx.transactionDate || (tx.date ? tx.date.slice(0, 10) : "—")} · {tx.remarks || tx.adjustmentReason || tx.issuedToName || tx.reason || "Stock update"}
                                            </div>
                                        </div>
                                        <span
                                            style={{
                                                display: "inline-flex",
                                                alignItems: "center",
                                                gap: 3,
                                                fontSize: 11.5,
                                                fontWeight: 700,
                                                padding: "2px 8px",
                                                borderRadius: 99,
                                                background: isPositive ? "#dcfce7" : "#fee2e2",
                                                color: isPositive ? "#15803d" : "#dc2626",
                                            }}
                                        >
                                            {isPositive ? <FiTrendingUp size={11} /> : <FiTrendingDown size={11} />}
                                            {tx.type} {tx.quantity} {tx.item?.unit || ""}
                                        </span>
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>

                {/* Low Stock Items */}
                <div className="card">
                    <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ display: "flex", alignItems: "center", gap: "0.4rem", fontWeight: 700 }}>
                            <FiAlertTriangle size={14} color="#dc2626" /> Low Stock Items
                        </span>
                        <button
                            className="btn btn-outline btn-sm"
                            onClick={() => navigate("/storekeeper/inventory/items")}
                            style={{ display: "flex", alignItems: "center", gap: "0.3rem", fontSize: 12 }}
                        >
                            View Items <FiArrowRight size={11} />
                        </button>
                    </div>
                    <div className="card-body" style={{ padding: 0 }}>
                        {lowStockItems.length === 0 ? (
                            <div style={{ padding: "1.5rem", textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
                                All item stock levels are healthy!
                            </div>
                        ) : (
                            lowStockItems.map((item, idx) => (
                                <div
                                    key={item._id || idx}
                                    style={{
                                        padding: "0.75rem 1rem",
                                        borderBottom: "1px solid var(--border)",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        fontSize: 13,
                                    }}
                                >
                                    <div>
                                        <div style={{ fontWeight: 600, color: "#991b1b" }}>{item.name}</div>
                                        <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                                            Category: {item.category?.name || item.category || "General"}
                                        </div>
                                    </div>
                                    <div style={{ textAlign: "right" }}>
                                        <span style={{ fontWeight: 700, color: "#dc2626" }}>
                                            {item.currentStock ?? item.quantity ?? 0} {item.unit || "units"} left
                                        </span>
                                        <div style={{ fontSize: 10.5, color: "var(--text-muted)" }}>
                                            Min: {item.minStockLevel ?? item.minQuantity ?? 5}
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}